import React, { useMemo, useState } from 'react';
import { TextInput } from '@mantine/core';
import { SectionCard } from '../../shared/SectionCard/SectionCard';
import { asCurrency, asHour } from '../../shared/utils/dashboard-page.utils';
import { CancellationTotals, SectionKey } from '../../shared/types/dashboard-page.types';
import { AdminApprovalRequestDetailView } from '../../../../services/admin-approval-requests/admin-approval-requests.service';
import classes from './cancellations-section.module.scss';

export type CancellationsSectionProps = {
  collapsedSections: Record<SectionKey, boolean>;
  toggleSection: (section: SectionKey) => void;
  cancellationTotals: CancellationTotals;
  cancelDetails: AdminApprovalRequestDetailView[];
};

const REASON_LABELS: Record<string, string> = {
  cancel_order: 'ביטול הזמנה',
  vip: 'VIP',
  waiter_error: 'טעות במנה',
  staff: 'שת"פ',
  other: 'אחר',
  manager_action: 'פעולת מנהל',
};

const getCancelCost = (detail: AdminApprovalRequestDetailView): number => detail.orderlines.reduce((sum, line) => sum + line.unitPrice * line.quantity, 0);

// order_code is "TenantName/sessionSeq/orderSeq" — the tenant name is redundant on a single-tenant dashboard, so show only "sessionSeq/orderSeq".
const getOrderNumber = (orderCode: string | null): string => (orderCode ? orderCode.split('/').slice(-2).join('/') : '—');

const getCancelItemsLabel = (detail: AdminApprovalRequestDetailView): string => detail.orderlines
  .map((line) => (line.quantity > 1 ? `${line.productName ?? 'פריט'} ${line.quantity}x` : (line.productName ?? 'פריט')))
  .join(', ');

const matchesSearch = (detail: AdminApprovalRequestDetailView, query: string): boolean => {
  if (!query) {
    return true;
  }
  const haystack = [
    detail.requestedByName,
    REASON_LABELS[detail.reason] ?? detail.reason,
    detail.note ?? '',
    detail.orderCode ?? '',
    detail.tableLabel ?? '',
    getCancelItemsLabel(detail),
  ].join(' ').toLowerCase();
  return haystack.includes(query.toLowerCase());
};

export const CancellationsSection: React.FC<CancellationsSectionProps> = ({
  collapsedSections,
  toggleSection,
  cancellationTotals,
  cancelDetails,
}) => {
  const [search, setSearch] = useState('');

  const visibleDetails = useMemo(
    () => cancelDetails.filter((detail) => matchesSearch(detail, search)),
    [cancelDetails, search],
  );

  return (
    <SectionCard
      title="ביטולים"
      sectionKey="cancellations"
      collapsed={collapsedSections.cancellations}
      onToggle={() => toggleSection('cancellations')}
      titleExtra={(
        <>
          <span className={classes.countBadge}>{`(${cancelDetails.length})`}</span>
          <span className={classes.costBadge}>{`עלות: ${asCurrency(cancellationTotals.itemsAmount)}`}</span>
        </>
      )}
    >
      <TextInput
        className={classes.searchInput}
        placeholder="חיפוש לפי הזמנה, מבטל, שולחן, פריטים, עלות..."
        value={search}
        onChange={(event) => setSearch(event.currentTarget.value)}
        data-testid="dashboard-cancellations-search"
      />

      {visibleDetails.length === 0 ? (
        <p className={classes.emptyHint}>אין ביטולים להצגה</p>
      ) : (
        <div className={classes.list}>
          {visibleDetails.map((detail) => (
            <div key={detail.id} className={classes.card}>
              <div className={classes.cell}>
                <span className={classes.cellLabel}>שעה</span>
                <span className={classes.cellValue}>{asHour(detail.createdAt)}</span>
              </div>
              <div className={classes.cell}>
                <span className={classes.cellLabel}>מבטל</span>
                <span className={classes.cellValue}>{detail.requestedByName}</span>
              </div>
              <div className={classes.cell}>
                <span className={classes.cellLabel}>סיבה</span>
                <span className={classes.cellValue}>{REASON_LABELS[detail.reason] ?? detail.reason}</span>
              </div>
              <div className={classes.cell}>
                <span className={classes.cellLabel}>הערה</span>
                <span className={classes.cellValue}>{detail.note ?? '—'}</span>
              </div>
              <div className={classes.cell}>
                <span className={classes.cellLabel}>הזמנה</span>
                <span className={classes.cellValue}>{getOrderNumber(detail.orderCode)}</span>
              </div>
              <div className={classes.cell}>
                <span className={classes.cellLabel}>שולחן</span>
                <span className={classes.cellValue}>{detail.tableLabel ?? '—'}</span>
              </div>
              <div className={classes.cell}>
                <span className={classes.cellLabel}>פריטים</span>
                <span className={classes.cellValue}>{getCancelItemsLabel(detail) || '—'}</span>
              </div>
              <div className={classes.cell}>
                <span className={classes.cellLabel}>עלות</span>
                <span className={`${classes.cellValue} ${classes.cellValueDanger}`}>{asCurrency(getCancelCost(detail))}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
};
